import type {
  DecisionEngineChannelConfig,
  DecisionEngineChannelType,
  DecisionEnginePreviewRecipientMode,
  DeliveryChannelType,
  DestinationSyncSettings,
  UserColumn,
  VariableDB,
} from "@hightouch/lib/customer-data/decision-engine/types";
import type { DestinationType, MessageTagMap } from "./sync-config-helpers";

/**
 * The destination framework category used by the sync engine:
 * - "trigger": Sends individual API requests per row (e.g. Braze campaign trigger, SFMC triggered send)
 * - "event": Sends event payloads that the destination processes (e.g. Klaviyo, Attentive)
 * - "batch": Uploads data in bulk to the destination (e.g. SFMC data extensions)
 */
export type DestinationCategory = "trigger" | "event" | "batch";

/**
 * Allows a value to be either static or derived from the channel config at runtime.
 * Used for fields like `supportsChannelLevelSync` that are static for most destinations
 * but config-dependent for others (e.g. SFMC DE depends on `useLegacyDataExtension`).
 */
export type MaybeConfigDriven<T> =
  | T
  | ((channelConfig: DecisionEngineChannelConfig) => T);

/**
 * What any producer of a generated message sync supplies. An AID channel and a
 * journey sync node both map onto this.
 */
export type GenericSyncSpec = {
  /** Id of the deciding entity: the AID flow, or the journey. Used for naming. */
  scopeId: string;
  /**
   * Id of the delivery unit inside the scope: the AID channel, or the journey
   * sync node. Today only SFMC data extensions reads it, for the data
   * extension key.
   */
  unitId: string;
  channelType: DecisionEngineChannelType;
  settings: DestinationSyncSettings;
  /** Fixed target campaign. Unset for channel syncs, which read `campaign_id` per row. */
  campaignId?: string | number;
  /** Spread over the generated config last. */
  syncConfigOverride?: Record<string, unknown>;
  /** Appended to the matching array properties of the generated config. */
  syncConfigAppend?: Record<string, unknown[]>;
};

export type SyncProducer = "aid" | "journey";

/** AID-only inputs. Every reader treats an absent value as empty or false. */
export type AidSyncSpec = {
  producer: "aid";
  /** Secondary destination resource, e.g. an SFMC journey key or an Eloqua template. Only the preview path passes it. */
  resourceId?: string | number;
  messageVariables?: VariableDB[];
  /** Map of message ID to key-value tags. Embedded in the Liquid template for destination payloads. */
  messageTags?: MessageTagMap;
  isPreview?: boolean;
  sendToHightouchUser?: boolean;
  previewRecipientMode?: DecisionEnginePreviewRecipientMode;
};

/**
 * A journey sync node supplies only the generic fields. The AID keys are
 * declared as undefined so definitions can destructure them unchanged, while
 * a journey spec can never carry a value for them.
 */
export type JourneySyncSpec = {
  producer: "journey";
  /** A journey sync always delivers, so the AID-only `raw` and `journey` channel types are excluded. */
  channelType: DeliveryChannelType;
  resourceId?: undefined;
  messageVariables?: undefined;
  messageTags?: undefined;
  isPreview?: undefined;
  sendToHightouchUser?: undefined;
  previewRecipientMode?: undefined;
};

export type SyncSpec = GenericSyncSpec & (AidSyncSpec | JourneySyncSpec);

/**
 * True for a preview send in registered recipient mode. Registered sends
 * must never create or mutate customer-data records (leads, user profiles,
 * attribute writes); ESP-side bookkeeping keyed by the preview address itself
 * is acceptable (e.g. SFMC's TSD AutoAddSubscribers auto-creating the
 * subscriber for the account address).
 */
export const isRegisteredPreviewSend = ({
  isPreview,
  previewRecipientMode,
}: Pick<SyncSpec, "isPreview" | "previewRecipientMode">): boolean =>
  Boolean(isPreview) && previewRecipientMode === "registered";

/**
 * The registered-preview key, trimmed. The settings UI trims before saving,
 * but the config is also writable via GraphQL directly, never key a send on
 * whitespace. A whitespace-only key resolves to undefined, so keyed branches
 * fall back to email keying.
 */
export const getPreviewRecipientKey = (
  settings: DestinationSyncSettings,
): string | undefined => settings.previewRecipientKey?.trim() || undefined;

export type GetSyncScheduleParams = {
  channelConfig: DecisionEngineChannelConfig;
  schedulingTimezone: string | null;
};

export type SyncSchedule = {
  type: string;
  schedule: { expression: string };
};

export type CreateResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  channelType: string;
  flowName: string;
  messageName: string;
  baseMessageId: string;
  destinationId: string;
  workspaceId: number;
};

/**
 * What a destination needs to accept a resource the marketer picked for a
 * flow message, before the pick is stored as the flow message's campaign.
 */
export type ValidateResourceParams = {
  workspaceId: number;
  channelType: string;
  flowId: string;
  parentModelId: string;
  resourceId: string;
};

export type UpdateResourceSchemaParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  campaignId: string;
  variables: VariableDB[];
  destinationId?: string;
  workspaceId?: number;
};

export type UpdateChannelResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  channelId: string;
  flowId: string;
  channelLabel?: string;
  variables: VariableDB[];
  userColumns: UserColumn[];
};

export type ListResourcesParams = {
  destinationConfig: any;
  destinationId: number;
  workspaceId: number;
  type: string;
  channelConfig: DecisionEngineChannelConfig;
  /** Scopes the listing to a parent resource when the destination supports it
   * (e.g. Marketo emails scoped to a smart campaign's parent program). */
  parentResourceId?: string;
  /** Filters the listing by name when the destination supports server-side
   * search (e.g. SFMC Content Builder assets). Ignored otherwise. */
  search?: string;
};

export type GetResourceParams = {
  destinationConfig: any;
  destinationId: number;
  workspaceId: number;
  resourceId: string;
  type: string;
  channelConfig: DecisionEngineChannelConfig;
};

export type ResolveContentResourceParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  campaignId: string;
};

export type ResolvedContentResource = {
  resourceId: string;
  type: string;
};

export type RenderResourceForUserParams = {
  destinationConfig: any;
  channelConfig: DecisionEngineChannelConfig;
  resourceId: string;
  /** The previewed user's channel identifier value (e.g. email or lead ID). */
  userIdentifier: string;
};

export type CheckDeliveryUserParams = Pick<
  RenderResourceForUserParams,
  "destinationConfig" | "channelConfig"
> & {
  testAddress: string;
};
export type DeliveryUserCheckResult = { exists: boolean; email: string | null };

export type RenderedResourceForUser =
  | { html: string }
  | { error: string; structuredError?: unknown };

/**
 * Resolve a `MaybeConfigDriven<T>` value. If it's a function, call it with channelConfig;
 * otherwise return it directly.
 */
export function resolveConfigDriven<T>(
  value: MaybeConfigDriven<T> | undefined,
  channelConfig: DecisionEngineChannelConfig,
  defaultValue: T,
): T {
  if (value === undefined) return defaultValue;
  if (typeof value === "function") {
    return (value as (c: DecisionEngineChannelConfig) => T)(channelConfig);
  }
  return value;
}

export type SyncTuningNumberField = {
  label: string;
  description: string;
  help?: string;
  min?: number;
  max: number;
  /** Hidden fields remain supported so saving preserves their existing overrides. */
  hidden?: boolean;
};

type SyncTuningBatchSizeField = SyncTuningNumberField & {
  configKey: "rowsPerBatch" | "batchSize";
};

export const SYNC_CONCURRENCY_HELP =
  "Adaptive controls may increase concurrency or reduce it when the destination throttles requests.";

/**
 * Which customer-tunable sync knobs the destination's rETL sync honors at
 * runtime, and the bounds for each (DE-3643). Knobs are persisted into
 * `channelConfig.syncConfigOverride`, which is spread over the generated sync
 * config last — so a knob is only meaningful if the rETL destination *reads*
 * that config key at runtime. An omitted knob (or an omitted descriptor
 * entirely) means the knob is rejected at save time.
 */
export type SyncTuningSupport = {
  /**
   * rETL sync `mode` values a customer may override to. Only list modes when
   * AID does not already emit a load-bearing mode for this destination (e.g.
   * Eloqua CDO requires `insert`, MongoDB requires `upsert`) — overriding
   * those would break the sync.
   */
  modes?: {
    label: string;
    description: string;
    options: readonly {
      value: "insert" | "update" | "upsert";
      label: string;
    }[];
  };
  /**
   * Batch-size knob. `configKey` is the rETL sync config key the destination
   * reads (usually `rowsPerBatch`; MongoDB uses `batchSize`).
   */
  batchSize?: SyncTuningBatchSizeField;
  /** Parallelization knob; the config key is always `parallelization`. */
  parallelization?: SyncTuningNumberField;
  sleepMs?: SyncTuningNumberField;
  notice?: string;
  /** `true` rather than a bound: a boolean has nothing to constrain. */
  useFtp?: true;
} & (
  | { processingBatchLimit?: undefined }
  | {
      processingBatchLimit: {
        max: number;
        defaultBatchSize: number;
        defaultParallelization: number;
      };
      batchSize: SyncTuningBatchSizeField;
      parallelization: SyncTuningNumberField;
    }
);

export function getSyncTuningProcessingBatchError(
  support: SyncTuningSupport | undefined,
  values: { batchSize?: number | null; parallelization?: number | null },
): string | undefined {
  if (!support?.processingBatchLimit) return undefined;
  const limit = support.processingBatchLimit;
  const batchSize = values.batchSize ?? limit.defaultBatchSize;
  const parallelization =
    values.parallelization ?? limit.defaultParallelization;
  if (batchSize * parallelization > limit.max) {
    return `"${support.batchSize.label}" multiplied by "${support.parallelization.label}" must not exceed ${limit.max.toLocaleString("en-US")}. Reduce either value.`;
  }
  return undefined;
}

/**
 * Resolve a definition's `syncTuning` descriptor for a specific channel.
 * Function form exists for destinations whose knob support depends on the
 * channel (e.g. OneSignal honors batch size only for in-app enrollment).
 */
export function resolveSyncTuningSupport(
  definition: DecisionEngineDestinationDefinition,
  params: {
    channelType: DecisionEngineChannelType;
    channelConfig: DecisionEngineChannelConfig;
  },
): SyncTuningSupport | undefined {
  const value = definition.syncTuning;
  return typeof value === "function" ? value(params) : value;
}

/**
 * Backend definition for a Decision Engine destination variant.
 *
 * Each destination+configType combination (e.g. "sfmc:trigger", "sfmc:dataExtensions")
 * gets its own definition. Only `generateSyncConfig` is required; everything else
 * has safe defaults (true/false/no-op).
 *
 * Definitions are looked up via `getDestinationDefinition(destinationType, settings)`
 * using composite keys: tries `${destinationType}:${settings.type}` first,
 * then falls back to `${destinationType}`.
 */
export interface DecisionEngineDestinationDefinition {
  destinationType: DestinationType;
  category: DestinationCategory;
  enrollmentChannelTypes?: DecisionEngineChannelType[];
  /** Producers this definition generates for. Omit to accept both. */
  producers?: readonly SyncProducer[];

  generateSyncConfig: (spec: SyncSpec) => Record<string, unknown>;

  syncTuning?:
    | SyncTuningSupport
    | ((params: {
        channelType: DecisionEngineChannelType;
        channelConfig: DecisionEngineChannelConfig;
      }) => SyncTuningSupport | undefined);

  syncStrategy?: {
    /** default: true */
    supportsChannelLevelSync?: MaybeConfigDriven<boolean>;
    /**
     * Whether enabling a message or channel creates and maintains a managed
     * sync on the destination. A destination that delivers another way, such
     * as native delivery through the CEP outbox, sets false. Default: true.
     */
    shouldManageSyncs?: MaybeConfigDriven<boolean>;
    getSyncSchedule?: (params: GetSyncScheduleParams) => SyncSchedule;
  };

  resourceLifecycle?: {
    /** default: false */
    requiresResourceUpdateOnVariableChange?: MaybeConfigDriven<boolean>;
    /** default: false */
    usesChannelLevelResourceUpdate?: MaybeConfigDriven<boolean>;
  };
}

/**
 * Extended definition that includes async resource lifecycle functions.
 * Only used by backend resolvers — never imported by the frontend app.
 */
export interface DecisionEngineBackendDefinition
  extends DecisionEngineDestinationDefinition {
  resourceLifecycle?: DecisionEngineDestinationDefinition["resourceLifecycle"] & {
    /** Rejects a picked resource the destination cannot deliver for; runs before the pick is stored. */
    validateResource?: (params: ValidateResourceParams) => Promise<void>;
    createResource?: (params: CreateResourceParams) => Promise<string>;
    updateResourceSchema?: (
      params: UpdateResourceSchemaParams,
    ) => Promise<void>;
    updateChannelResource?: (
      params: UpdateChannelResourceParams,
    ) => Promise<void>;
  };

  resources?: {
    listResources?: (params: ListResourcesParams) => Promise<any[]>;
    getResource?: (params: GetResourceParams) => Promise<any>;
    /** Source inspection must preserve merge tokens and unresolved shared references. */
    getSnippetSource?: (params: GetResourceParams) => Promise<{
      html: string;
      hasUnexpandedReferences: boolean;
    } | null>;
    resolveContentResource?: (
      params: ResolveContentResourceParams,
    ) => Promise<ResolvedContentResource | null>;
    checkDeliveryUser?: (
      params: CheckDeliveryUserParams,
    ) => Promise<DeliveryUserCheckResult>;
    /**
     * For destinations whose templating executes destination-side: the
     * message-preview render-API path uses this to preview without sending.
     */
    renderResourceForUser?: (
      params: RenderResourceForUserParams,
    ) => Promise<RenderedResourceForUser>;
  };
}
